'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { useFeatureStatus } from '@/hooks/useFeatureStatus';
import { useAuth } from './AuthContext';

interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
}

interface FeatureStatusContextType {
  imageGeneration: boolean;
  videoGeneration: boolean;
  chatGeneration: boolean;
  /** Admin-editable chat empty-state suggestion chips. */
  chatSuggestions: string[];
  /** Admin-editable post-upload suggestion chips (up to 4). */
  uploadSuggestions: string[];
  maintenance: MaintenanceStatus;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const FeatureStatusContext = createContext<FeatureStatusContextType | undefined>(undefined);

export function FeatureStatusProvider({ children }: { children: React.ReactNode }) {
  const { features, maintenance, loading, error, refetch } = useFeatureStatus();
  const { user } = useAuth();

  const isAdmin = user?.isAdmin === true;

  const value = useMemo(() => ({
    imageGeneration: isAdmin ? true : features.imageGeneration,
    videoGeneration: isAdmin ? true : features.videoGeneration,
    chatGeneration: isAdmin ? true : features.chatGeneration,
    // Suggestion chips are not access-gated — admins see the same list users do.
    chatSuggestions: features.chatSuggestions,
    uploadSuggestions: features.uploadSuggestions,
    maintenance: isAdmin ? { enabled: false, message: null } : maintenance,
    loading,
    error,
    refetch,
  }), [features.imageGeneration, features.videoGeneration, features.chatGeneration, features.chatSuggestions, features.uploadSuggestions, maintenance, loading, error, refetch, isAdmin]);

  return (
    <FeatureStatusContext.Provider value={value}>
      {children}
    </FeatureStatusContext.Provider>
  );
}

export function useFeatures() {
  const context = useContext(FeatureStatusContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeatureStatusProvider');
  }
  return context;
}
